// Motion building blocks for the signed-in customer pages. Everything here animates with
// motion values or CSS transforms, so nothing re-renders per frame.
import { useEffect, useMemo, useRef, type ReactNode } from "react";
import {
  MotionConfig, animate, motion, useMotionValue, useReducedMotion, useSpring, useTransform, type HTMLMotionProps, type Variants,
} from "motion/react";
import { useApi } from "../../api.ts";
import { cn } from "../../ui/index.tsx";
import type { Game } from "../../../shared/types.ts";

export const EASE: [number, number, number, number] = [0.2, 0.8, 0.2, 1];

/** True when the user asked for reduced motion; use it to drop loops, particles and large movement. */
export const useCalm = () => !!useReducedMotion();

/** Wrap a page: transform and layout animations switch off automatically for reduced-motion users. */
export function Stage({ children }: { children: ReactNode }) {
  return <MotionConfig reducedMotion="user">{children}</MotionConfig>;
}

export const stagger = (each = 0.07, delay = 0): Variants => ({ hidden: {}, show: { transition: { staggerChildren: each, delayChildren: delay } } });
export const rise: Variants = { hidden: { opacity: 0, y: 18 }, show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: EASE } } };
export const slideIn: Variants = { hidden: { opacity: 0, x: -28 }, show: { opacity: 1, x: 0, transition: { duration: 0.5, ease: EASE } } };
export const pop: Variants = { hidden: { opacity: 0, scale: 0.8 }, show: { opacity: 1, scale: 1, transition: { type: "spring", stiffness: 380, damping: 22 } } };

/** Games keyed by id, for art and category lookups. */
export function useGameMap(): Map<string, Game> {
  const games = useApi<Game[]>("/games");
  return useMemo(() => new Map((games.data ?? []).map((g) => [g.id, g])), [games.data]);
}

/** Springy hover and press for buttons and links. */
export function Springy({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <motion.span className={cn("inline-block", className)} whileHover={{ scale: 1.045, y: -1 }} whileTap={{ scale: 0.94 }}
      transition={{ type: "spring", stiffness: 420, damping: 18 }}>
      {children}
    </motion.span>
  );
}

/** Counts up from 0 whenever `value` changes. */
export function CountUp({ value, duration = 1.1, suffix = "", className }: { value: number; duration?: number; suffix?: string; className?: string }) {
  const calm = useCalm();
  const mv = useMotionValue(calm ? value : 0);
  const text = useTransform(mv, (v) => `${Math.round(v).toLocaleString()}${suffix}`);
  useEffect(() => {
    if (calm) { mv.set(value); return; }
    const controls = animate(mv, value, { duration, ease: EASE });
    return () => controls.stop();
  }, [value, calm, duration, mv]);
  return <motion.span className={className}>{text}</motion.span>;
}

const GLYPHS = "ABCDEF0123456789#$%&*<>/\\=+";

/** Decrypt-style text reveal. Writes to the DOM directly (no React state) and reserves the final width. */
export function Scramble({ text, delay = 0.3, duration = 0.9, className }: { text: string; delay?: number; duration?: number; className?: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  const calm = useCalm();
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (calm) { el.textContent = text; return; }
    const t0 = performance.now() + delay * 1000;
    let raf = 0;
    let last = 0;
    const tick = (now: number) => {
      const p = Math.min(1, Math.max(0, (now - t0) / (duration * 1000)));
      if (p >= 1) { el.textContent = text; return; }
      if (now - last > 45) {
        last = now;
        const reveal = Math.floor(p * text.length);
        let out = "";
        for (let i = 0; i < text.length; i++) out += i < reveal || text[i] === " " ? text[i] : GLYPHS[Math.floor(Math.random() * GLYPHS.length)];
        el.textContent = out;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [text, delay, duration, calm]);
  return (
    <span className="relative inline-block max-w-full align-bottom">
      <span className="invisible block truncate">{text}</span>
      <span ref={ref} aria-hidden className={cn("absolute inset-0 block truncate", className)} />
      <span className="sr-only">{text}</span>
    </span>
  );
}

/** 3D hover tilt with a pointer-following spotlight. Pass `variants` to take part in a parent's stagger. */
export function Tilt({ children, className, max = 6, ...rest }: { max?: number } & Omit<HTMLMotionProps<"div">, "style" | "onPointerMove" | "onPointerLeave">) {
  const calm = useCalm();
  const rx = useSpring(0, { stiffness: 220, damping: 20 });
  const ry = useSpring(0, { stiffness: 220, damping: 20 });
  return (
    <motion.div
      {...rest}
      className={cn("spotlight", className)}
      style={{ rotateX: rx, rotateY: ry, transformPerspective: 900 }}
      whileHover={calm ? undefined : { y: -3 }}
      onPointerMove={(e) => {
        if (e.pointerType !== "mouse") return;
        const r = e.currentTarget.getBoundingClientRect();
        const px = (e.clientX - r.left) / r.width - 0.5;
        const py = (e.clientY - r.top) / r.height - 0.5;
        e.currentTarget.style.setProperty("--mx", `${e.clientX - r.left}px`);
        e.currentTarget.style.setProperty("--my", `${e.clientY - r.top}px`);
        if (!calm) { ry.set(px * max * 2); rx.set(-py * max * 2); }
      }}
      onPointerLeave={() => { rx.set(0); ry.set(0); }}
    >
      {children}
    </motion.div>
  );
}

/** Circular progress ring that draws itself in. `fraction` is 0..1. */
export function Ring({ fraction, size = 72, stroke = 5, color = "var(--color-ok)", glow, delay = 0, children, className }: {
  fraction: number; size?: number; stroke?: number; color?: string; glow?: boolean; delay?: number; children?: ReactNode; className?: string;
}) {
  const r = (size - stroke) / 2;
  const f = Math.min(1, Math.max(0, fraction));
  return (
    <div className={cn("relative shrink-0", className)} style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="-rotate-90" aria-hidden
        style={glow ? { filter: `drop-shadow(0 0 6px ${color})` } : undefined}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--color-line)" strokeWidth={stroke} />
        <motion.circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={stroke} strokeLinecap="round"
          initial={{ pathLength: 0 }} animate={{ pathLength: f }} transition={{ duration: 1.3, delay, ease: EASE }} />
      </svg>
      <div className="absolute inset-0 grid place-items-center">{children}</div>
    </div>
  );
}

/** Slow-drifting colour blobs behind a hero. Parent needs `relative overflow-hidden`. */
export function Aurora({ className }: { className?: string }) {
  const calm = useCalm();
  const drift = (x: number[], y: number[], d: number) =>
    calm ? undefined : { animate: { x, y }, transition: { duration: d, repeat: Infinity, ease: "easeInOut" as const } };
  return (
    <div className={cn("pointer-events-none absolute inset-0 overflow-hidden", className)} aria-hidden>
      <motion.div className="absolute -left-20 -top-24 size-72 rounded-full bg-accent/20 blur-3xl" {...drift([0, 50, -20, 0], [0, 30, 50, 0], 16)} />
      <motion.div className="absolute -bottom-28 right-[-4rem] size-80 rounded-full bg-ember/[0.14] blur-3xl" {...drift([0, -40, 20, 0], [0, -30, 10, 0], 19)} />
      <motion.div className="absolute left-1/3 top-1/2 size-64 rounded-full bg-[#1d1848]/60 blur-3xl" {...drift([0, 30, -40, 0], [0, -20, 30, 0], 22)} />
    </div>
  );
}

const BURST_COLORS = ["#ff3d4f", "#ff8a3d", "#ffd2a8", "#2ee58a", "#4da3ff", "#ffffff"];

/** Confetti-style burst in brand colours. Re-fires whenever `id` changes to a new non-zero value. Parent needs `relative`. */
export function Burst({ id, count = 36 }: { id: number; count?: number }) {
  const calm = useCalm();
  const parts = useMemo(() => Array.from({ length: count }, (_, i) => {
    const a = (i / count) * Math.PI * 2 + Math.random() * 0.5;
    const d = 80 + Math.random() * 130;
    return {
      x: Math.cos(a) * d, y: Math.sin(a) * d * 0.8 + 40, rot: (Math.random() - 0.5) * 720,
      w: 4 + Math.random() * 6, h: 4 + Math.random() * 9, c: BURST_COLORS[i % BURST_COLORS.length]!,
      dur: 0.9 + Math.random() * 0.7, round: i % 3 === 0,
    };
  }), [id, count]);
  if (!id || calm) return null;
  return (
    <div key={id} className="pointer-events-none absolute inset-0 z-10 grid place-items-center overflow-visible" aria-hidden>
      <motion.span className="absolute size-16 rounded-full border-2 border-ok" initial={{ scale: 0.2, opacity: 0.9 }} animate={{ scale: 7, opacity: 0 }} transition={{ duration: 0.9, ease: "easeOut" }} />
      <motion.span className="absolute size-16 rounded-full border border-accent" initial={{ scale: 0.2, opacity: 0.9 }} animate={{ scale: 10, opacity: 0 }} transition={{ duration: 1.2, delay: 0.1, ease: "easeOut" }} />
      {parts.map((p, i) => (
        <motion.span key={i} className="absolute" style={{ width: p.w, height: p.h, background: p.c, borderRadius: p.round ? 999 : 2 }}
          initial={{ x: 0, y: 0, opacity: 1, scale: 1, rotate: 0 }}
          animate={{ x: p.x, y: p.y, opacity: 0, scale: 0.4, rotate: p.rot }}
          transition={{ duration: p.dur, ease: [0.1, 0.7, 0.3, 1] }} />
      ))}
    </div>
  );
}
